import { LiveData, Service } from '@toeverything/infra';

/**
 * FullscreenService - Centralized Fullscreen Management (SOLID)
 *
 * Single Responsibility: Manages fullscreen state for presentation mode.
 * Decouples fullscreen logic from PresentationToolbar's effect chain.
 *
 * Why this exists:
 * - PresentationToolbar's effect-based fullscreen trigger has race conditions
 * - Toolbar only exists when isPresentMode=true (conditional rendering)
 * - connectedCallback timing is non-deterministic with Lit's async rendering
 * - This service provides explicit, reliable fullscreen control
 */
export class FullscreenService extends Service {
  /**
   * Observable fullscreen state
   */
  readonly isFullscreen$ = new LiveData<boolean>(false);

  /**
   * The element to make fullscreen (usually the viewport element)
   */
  private targetElement: Element | null = null;

  constructor() {
    super();
    this.setupFullscreenListener();
  }

  /**
   * Listen to fullscreen changes (user can exit via ESC)
   */
  private setupFullscreenListener(): void {
    const handler = () => {
      const isFullscreen = !!document.fullscreenElement;
      this.isFullscreen$.next(isFullscreen);
    };

    document.addEventListener('fullscreenchange', handler);
    this.disposables.push(() => {
      document.removeEventListener('fullscreenchange', handler);
    });
  }

  /**
   * Bind the element that should go fullscreen.
   * Called when edgeless container is bound.
   *
   * @returns Cleanup function
   */
  bindElement(element: Element): () => void {
    this.targetElement = element;
    return () => {
      this.targetElement = null;
    };
  }

  /**
   * Request fullscreen on the bound element.
   * Handles cross-browser compatibility.
   *
   * @returns true if fullscreen was entered successfully
   */
  async enterFullscreen(): Promise<boolean> {
    console.log(
      '[FullscreenService] enterFullscreen called, targetElement:',
      this.targetElement
    );
    if (!this.targetElement) {
      console.warn('[FullscreenService] No element bound');
      return false;
    }

    // Already in fullscreen
    if (document.fullscreenElement) {
      this.isFullscreen$.next(true);
      return true;
    }

    try {
      console.log('[FullscreenService] Requesting fullscreen on:', {
        tagName: this.targetElement.tagName,
        isConnected: this.targetElement.isConnected,
        hasRequestFullscreen: !!this.targetElement.requestFullscreen,
      });
      await this.requestFullscreenCrossBrowser(this.targetElement);
      console.log('[FullscreenService] Fullscreen request succeeded');
      this.isFullscreen$.next(true);
      return true;
    } catch (error) {
      const err = error as Error;
      console.error('[FullscreenService] Failed to enter fullscreen:', {
        name: err?.name,
        message: err?.message,
        element: this.targetElement?.tagName,
        isConnected: this.targetElement?.isConnected,
        isVisible: this.targetElement
          ? getComputedStyle(this.targetElement).display !== 'none'
          : false,
      });
      return false;
    }
  }

  /**
   * Exit fullscreen mode.
   */
  async exitFullscreen(): Promise<void> {
    if (!document.fullscreenElement) {
      this.isFullscreen$.next(false);
      return;
    }

    try {
      await document.exitFullscreen();
      this.isFullscreen$.next(false);
    } catch (error) {
      console.error('[FullscreenService] Failed to exit fullscreen:', error);
    }
  }

  /**
   * Toggle fullscreen state.
   *
   * @returns true if now in fullscreen, false otherwise
   */
  async toggleFullscreen(): Promise<boolean> {
    if (document.fullscreenElement) {
      await this.exitFullscreen();
      return false;
    } else {
      return this.enterFullscreen();
    }
  }

  /**
   * Cross-browser fullscreen request.
   * Handles Firefox (mozRequestFullScreen) and Safari (webkitRequestFullscreen).
   */
  private async requestFullscreenCrossBrowser(element: Element): Promise<void> {
    if (element.requestFullscreen) {
      await element.requestFullscreen();
    } else if (
      'mozRequestFullScreen' in element &&
      element.mozRequestFullScreen instanceof Function
    ) {
      // Firefox
      element.mozRequestFullScreen();
    } else if (
      'webkitRequestFullscreen' in element &&
      element.webkitRequestFullscreen instanceof Function
    ) {
      // Safari
      element.webkitRequestFullscreen();
    } else if (
      'msRequestFullscreen' in element &&
      element.msRequestFullscreen instanceof Function
    ) {
      // IE/Edge (legacy)
      element.msRequestFullscreen();
    } else {
      throw new Error('Fullscreen API not supported');
    }
  }

  override dispose(): void {
    this.targetElement = null;
    super.dispose();
  }
}
